import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BACKEND_URL from '../../../api';
import '../../../Admin.css';
import loading from '../../../images/spin.gif';
import countryList from '../../../countryCodes.json';
import { country_code_array2 } from '../functions';
import { useCountry } from '../../../context/CountryContext';

export default function CountryManagement() {
    const { selectedCountry } = useCountry();
    const navigate = useNavigate();
    const PORTAL_BASE_URL = 'https://parcels.shypv.com/';
    
    // Common currencies list
    const currencyList = [
        'AED', 'USD', 'EUR', 'GBP', 'SAR', 'QAR', 'OMR', 'KWD', 'BHD',
        'INR', 'PKR', 'BDT', 'LKR', 'NPR', 'CNY', 'JPY', 'AUD', 'CAD',
        'CHF', 'SEK', 'NOK', 'DKK', 'ZAR', 'TRY', 'MXN', 'BRL', 'RUB'
    ];
    
    // Get all countries for transit time selection
    const allCountries = country_code_array2();
    
    const [countries, setCountries] = useState([]);
    const [logoPreview, setLogoPreview] = useState(null);
    const [partnerLogoPreview, setPartnerLogoPreview] = useState(null);
    const [cityInput, setCityInput] = useState('');
    const [cities, setCities] = useState([]);
    const [transitTimeCountry, setTransitTimeCountry] = useState('');
    const [transitTimeDays, setTransitTimeDays] = useState('');
    const [transitTimes, setTransitTimes] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const [message, setMessage] = useState('');
    const [activeTab, setActiveTab] = useState('basic');
    const [uploadingRateCard, setUploadingRateCard] = useState(false);
    const [rateCardUploadStatus, setRateCardUploadStatus] = useState({});

    const [formData, setFormData] = useState({
        id: null,
        countryName: '',
        portalUrl: 'https://parcels.shypv.com/',// For editing the suffix only
        logoUrl: '',
        phone: '',
        whatsapp: '',
        citiesToCollect: '',
        // Carrier-specific rate tables
        dhlExportRateUrl: '',
        dhlImportRateUrl: '',
        fedexExportRateUrl: '',
        fedexImportRateUrl: '',
        upsExportRateUrl: '',
        upsImportRateUrl: '',
        otherExportRateUrl: '',
        otherImportRateUrl: '',
        carrier: 'DHL',
        transitTime: '',
        weightUnit: 'kg',
        dimensionUnit: 'cm',
        currency: '',
        shypvCommission: 2.0,
        paymentGatewayCurrency: '',
        labelPartnerLogoUrl: '',
        termsAndConditions: '',
        isActive: 1,
        isDefault: 0
    });

    useEffect(() => {
        fetchCountries();
    }, []);

    const fetchCountries = async () => {
        setLoadingData(true);
        try {
            const response = await fetch(`${BACKEND_URL}/countries`);
            const data = await response.json();
            if (data.status === 'success') {
                setCountries(data.data);
            }
        } catch (error) {
            console.error('Error fetching countries:', error);
        } finally {
            setLoadingData(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name === 'portalUrl') {
            // Construct full portal URL from base + suffix
            // If value is empty, use base URL without trailing slash (for UAE)
            // Otherwise, add the suffix with a slash
            const cleanValue = value.trim();
            setFormData({
                ...formData,
                portalUrl: cleanValue ? `https://parcels.shypv.com/${cleanValue}` : 'https://parcels.shypv.com'
            });
        } else {
            setFormData({
                ...formData,
                [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
            });
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setMessage('Please upload an image file');
            return;
        }

        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            setMessage('File size should be less than 2MB');
            return;
        }

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setLogoPreview(reader.result);
        };
        reader.readAsDataURL(file);

        // For now, store as data URL. In production, upload to server
        // You can implement server upload here
        const logoDataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(file);
        });

        setFormData({
            ...formData,
            logoUrl: logoDataUrl
        });
    };

    const handlePartnerLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setMessage('Please upload an image file');
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            setMessage('File size should be less than 2MB');
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setPartnerLogoPreview(reader.result);
        };
        reader.readAsDataURL(file);

        const logoDataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(file);
        });

        setFormData({
            ...formData,
            labelPartnerLogoUrl: logoDataUrl
        });
    };

    const handleAddCity = () => {
        if (cityInput.trim()) {
            // Check if city already exists (case-insensitive)
            const cityExists = cities.some(city => 
                city.toLowerCase() === cityInput.trim().toLowerCase()
            );
            
            if (cityExists) {
                setMessage(`City "${cityInput.trim()}" already exists in the list.`);
                setTimeout(() => setMessage(''), 3000);
                setCityInput('');
                return;
            }
            
            const updatedCities = [...cities, cityInput.trim()];
            setCities(updatedCities);
            setFormData({
                ...formData,
                citiesToCollect: updatedCities.join(', ')
            });
            setCityInput('');
        }
    };

    const handleRemoveCity = (index) => {
        const updatedCities = cities.filter((_, i) => i !== index);
        setCities(updatedCities);
        setFormData({
            ...formData,
            citiesToCollect: updatedCities.join(', ')
        });
    };

    const handleClearAllCities = () => {
        if (window.confirm('Are you sure you want to clear all cities?')) {
            setCities([]);
            setFormData({
                ...formData,
                citiesToCollect: ''
            });
        }
    };

    const handleDownloadCityTemplate = () => {
        // Create CSV template
        const csvContent = "City Name\nDubai\nAbu Dhabi\nSharjah";
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'cities_template.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleCityFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.name.endsWith('.csv')) {
            setMessage('Please upload a CSV file');
            setTimeout(() => setMessage(''), 3000);
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const text = event.target.result;
                const lines = text.split('\n');
                
                // Skip header row and process cities
                const newCities = [];
                for (let i = 1; i < lines.length; i++) {
                    const line = lines[i].trim();
                    if (line) {
                        // Handle CSV with quotes and commas
                        const city = line.replace(/^"|"$/g, '').trim();
                        if (city && !newCities.includes(city)) {
                            newCities.push(city);
                        }
                    }
                }

                if (newCities.length === 0) {
                    setMessage('No valid cities found in the file');
                    setTimeout(() => setMessage(''), 3000);
                    return;
                }

                // Merge with existing cities, avoiding duplicates
                const mergedCities = [...cities];
                let addedCount = 0;
                
                newCities.forEach(city => {
                    if (!mergedCities.some(c => c.toLowerCase() === city.toLowerCase())) {
                        mergedCities.push(city);
                        addedCount++;
                    }
                });

                setCities(mergedCities);
                setFormData({
                    ...formData,
                    citiesToCollect: mergedCities.join(', ')
                });

                setMessage(`Successfully added ${addedCount} cities from file`);
                setTimeout(() => setMessage(''), 3000);
            } catch (error) {
                setMessage('Error reading file: ' + error.message);
                setTimeout(() => setMessage(''), 3000);
            }
        };

        reader.onerror = () => {
            setMessage('Error reading file');
            setTimeout(() => setMessage(''), 3000);
        };

        reader.readAsText(file);
        // Reset file input
        e.target.value = '';
    };

    const handleAddTransitTime = () => {
        if (transitTimeCountry.trim() && transitTimeDays.trim()) {
            // Check if country already exists
            const countryExists = transitTimes.some(tt => tt.country === transitTimeCountry.trim());
            
            if (countryExists) {
                setMessage(`Transit time for ${transitTimeCountry} already exists. Please remove it first to update.`);
                setTimeout(() => setMessage(''), 3000);
                return;
            }
            
            const updatedTransitTimes = [...transitTimes, { 
                country: transitTimeCountry.trim(), 
                days: transitTimeDays.trim() 
            }];
            setTransitTimes(updatedTransitTimes);
            
            // Convert to JSON object
            const transitTimeObj = {};
            updatedTransitTimes.forEach(tt => {
                transitTimeObj[tt.country] = tt.days;
            });
            
            setFormData({
                ...formData,
                transitTime: JSON.stringify(transitTimeObj)
            });
            
            setTransitTimeCountry('');
            setTransitTimeDays('');
        }
    };

    const handleRemoveTransitTime = (index) => {
        const updatedTransitTimes = transitTimes.filter((_, i) => i !== index);
        setTransitTimes(updatedTransitTimes);
        
        const transitTimeObj = {};
        updatedTransitTimes.forEach(tt => {
            transitTimeObj[tt.country] = tt.days;
        });
        
        setFormData({
            ...formData,
            transitTime: JSON.stringify(transitTimeObj)
        });
    };

    const handleDownloadTransitTimeTemplate = () => {
        // Create CSV template with sample data
        const csvContent = "Country/Region,Transit Time (Days)\nUnited States,3-5 days\nUnited Kingdom,2-4 days\nCanada,4-6 days\nAustralia,5-7 days\nGermany,3-5 days\nFrance,3-5 days\nItaly,4-6 days\nSpain,4-6 days\nJapan,5-7 days\nChina,7-10 days\nIndia,5-8 days\nSaudi Arabia,2-3 days\nUnited Arab Emirates,1-2 days";
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'transit_times_template.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleTransitTimeFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.name.endsWith('.csv')) {
            setMessage('Please upload a CSV file');
            setTimeout(() => setMessage(''), 3000);
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const text = event.target.result;
                const lines = text.split('\n');
                
                // Skip header row and process transit times
                const newTransitTimes = [];
                for (let i = 1; i < lines.length; i++) {
                    const line = lines[i].trim();
                    if (line) {
                        // Improved CSV parsing to handle quoted values and commas within values
                        let parts = [];
                        let currentPart = '';
                        let inQuotes = false;
                        
                        for (let j = 0; j < line.length; j++) {
                            const char = line[j];
                            if (char === '"') {
                                inQuotes = !inQuotes;
                            } else if (char === ',' && !inQuotes) {
                                parts.push(currentPart.trim());
                                currentPart = '';
                            } else {
                                currentPart += char;
                            }
                        }
                        parts.push(currentPart.trim()); // Add the last part
                        
                        // Remove quotes from parts
                        parts = parts.map(part => part.replace(/^"|"$/g, ''));
                        
                        if (parts.length >= 2) {
                            const country = parts[0];
                            const days = parts.slice(1).join(',').trim(); // Join remaining parts in case transit time has commas
                            if (country && days) {
                                // Check for duplicates
                                if (!newTransitTimes.some(tt => tt.country.toLowerCase() === country.toLowerCase())) {
                                    newTransitTimes.push({ country, days });
                                }
                            }
                        }
                    }
                }

                if (newTransitTimes.length === 0) {
                    setMessage('No valid transit times found in the file');
                    setTimeout(() => setMessage(''), 3000);
                    return;
                }

                // Merge with existing transit times, avoiding duplicates
                const mergedTransitTimes = [...transitTimes];
                let addedCount = 0;
                let updatedCount = 0;
                
                newTransitTimes.forEach(newTT => {
                    const existingIndex = mergedTransitTimes.findIndex(
                        tt => tt.country.toLowerCase() === newTT.country.toLowerCase()
                    );
                    if (existingIndex >= 0) {
                        // Update existing
                        mergedTransitTimes[existingIndex] = newTT;
                        updatedCount++;
                    } else {
                        // Add new
                        mergedTransitTimes.push(newTT);
                        addedCount++;
                    }
                });

                setTransitTimes(mergedTransitTimes);
                
                // Convert to JSON object
                const transitTimeObj = {};
                mergedTransitTimes.forEach(tt => {
                    transitTimeObj[tt.country] = tt.days;
                });
                
                setFormData({
                    ...formData,
                    transitTime: JSON.stringify(transitTimeObj)
                });

                setMessage(`Successfully processed ${newTransitTimes.length} transit times (${addedCount} added, ${updatedCount} updated)`);
                setTimeout(() => setMessage(''), 5000);
            } catch (error) {
                setMessage('Error reading file: ' + error.message);
                setTimeout(() => setMessage(''), 3000);
            }
        };

        reader.onerror = () => {
            setMessage('Error reading file');
            setTimeout(() => setMessage(''), 3000);
        };

        reader.readAsText(file);
        // Reset file input
        e.target.value = '';
    };

    const handleClearAllTransitTimes = () => {
        if (window.confirm('Are you sure you want to clear all transit times?')) {
            setTransitTimes([]);
            setFormData({
                ...formData,
                transitTime: ''
            });
        }
    };

    const handleDownloadRateCardTemplate = (carrier, rateType, itemType) => {
        // Create a sample Excel file structure as CSV (can be converted to Excel)
        // This is a template showing the expected format
        const templateData = [
            ['Weight (KG)', 'Zone 1', 'Zone 2', 'Zone 3', 'Zone 4', 'Zone 5', 'Zone 6', 'Zone 7', 'Zone 8', 'Zone 9'],
            ['0.5', '44.36', '65.30', '42.71', '50.00', '55.00', '60.00', '65.00', '70.00', '45.00'],
            ['1.0', '50.00', '70.00', '48.00', '55.00', '60.00', '65.00', '70.00', '75.00', '50.00'],
            ['1.5', '55.00', '75.00', '53.00', '60.00', '65.00', '70.00', '75.00', '80.00', '55.00'],
            ['2.0', '60.00', '80.00', '58.00', '65.00', '70.00', '75.00', '80.00', '85.00', '60.00'],
            ['2.5', '65.00', '85.00', '63.00', '70.00', '75.00', '80.00', '85.00', '90.00', '65.00'],
            ['3.0', '70.00', '90.00', '68.00', '75.00', '80.00', '85.00', '90.00', '95.00', '70.00'],
            ['5.0', '85.00', '110.00', '83.00', '90.00', '95.00', '100.00', '105.00', '110.00', '85.00'],
            ['10.0', '120.00', '150.00', '118.00', '125.00', '130.00', '135.00', '140.00', '145.00', '120.00'],
            ['20.0', '200.00', '250.00', '198.00', '205.00', '210.00', '215.00', '220.00', '225.00', '200.00'],
            ['30.0', '280.00', '350.00', '278.00', '285.00', '290.00', '295.00', '300.00', '305.00', '280.00'],
            ['', '', '', '', '', '', '', '', '', ''],
            ['Adder rate per additional 0.5 KG from 2.6 KG', '', '', '', '', '', '', '', '', ''],
            ['From', 'To', 'Zone 1', 'Zone 2', 'Zone 3', 'Zone 4', 'Zone 5', 'Zone 6', 'Zone 7', 'Zone 8', 'Zone 9'],
            ['2.6', '30.0', '5.53', '7.96', '8.30', '7.62', '10.21', '11.94', '13.26', '15.92', '7.96'],
            ['', '', '', '', '', '', '', '', '', ''],
            ['Multiplier rate per 1 KG from 30.1 KG', '', '', '', '', '', '', '', '', ''],
            ['From', 'To', 'Zone 1', 'Zone 2', 'Zone 3', 'Zone 4', 'Zone 5', 'Zone 6', 'Zone 7', 'Zone 8', 'Zone 9'],
            ['30.1', '50.0', '14.53', '16.42', '17.84', '16.41', '22.95', '26.53', '29.18', '35.82', '17.84'],
            ['50.1', '70.0', '13.85', '14.34', '17.84', '16.74', '22.95', '25.50', '27.86', '35.71', '17.84'],
            ['70.1', '300.0', '13.85', '12.75', '15.28', '15.29', '20.39', '25.50', '27.86', '35.70', '15.28']
        ];

        // Convert to CSV format
        const csvContent = templateData.map(row => 
            row.map(cell => `"${cell}"`).join(',')
        ).join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${carrier}_${rateType}_${itemType}_rate_card_template.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleRateCardUpload = async (carrier, rateType, itemType, file) => {
        if (!file) return;
        
        // Validate file type
        const validExtensions = ['.xlsx', '.xls'];
        const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        if (!validExtensions.includes(fileExtension)) {
            setMessage('Please upload an Excel file (.xlsx or .xls)');
            setTimeout(() => setMessage(''), 3000);
            return;
        }

        setUploadingRateCard(true);
        const uploadKey = `${carrier}_${rateType}_${itemType}`;
        setRateCardUploadStatus({ ...rateCardUploadStatus, [uploadKey]: 'uploading' });

        try {
            const uploadFormData = new FormData();
            uploadFormData.append('file', file);
            uploadFormData.append('carrier', carrier);
            uploadFormData.append('rateType', rateType); // 'export' or 'import'
            uploadFormData.append('itemType', itemType); // 'parcel' or 'document'
            uploadFormData.append('countryId', formData.id || '');

            const response = await fetch(`${BACKEND_URL}/countries/rate-card/upload`, {
                method: 'POST',
                body: uploadFormData
            });

            const data = await response.json();

            if (data.status === 'success') {
                setMessage(`Rate card uploaded successfully for ${carrier} ${rateType} ${itemType}`);
                setRateCardUploadStatus({ ...rateCardUploadStatus, [uploadKey]: 'success' });
                setTimeout(() => {
                    setMessage('');
                    setRateCardUploadStatus({ ...rateCardUploadStatus, [uploadKey]: '' });
                }, 5000);
            } else {
                setMessage(data.message || 'Error uploading rate card');
                setRateCardUploadStatus({ ...rateCardUploadStatus, [uploadKey]: 'error' });
                setTimeout(() => {
                    setMessage('');
                    setRateCardUploadStatus({ ...rateCardUploadStatus, [uploadKey]: '' });
                }, 5000);
            }
        } catch (error) {
            setMessage('Error: ' + error.message);
            setRateCardUploadStatus({ ...rateCardUploadStatus, [uploadKey]: 'error' });
            setTimeout(() => {
                setMessage('');
                setRateCardUploadStatus({ ...rateCardUploadStatus, [uploadKey]: '' });
            }, 5000);
        } finally {
            setUploadingRateCard(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoadingData(true);
        setMessage('');

        try {
            // Prepare data to send - ensure cities and transit times are in correct format
            const dataToSend = {
                ...formData,
                // Ensure citiesToCollect is a comma-separated string
                citiesToCollect: cities.length > 0 ? cities.join(', ') : formData.citiesToCollect,
                // Ensure transitTime is a JSON string
                transitTime: transitTimes.length > 0 ? JSON.stringify(
                    transitTimes.reduce((obj, tt) => {
                        obj[tt.country] = tt.days;
                        return obj;
                    }, {})
                ) : formData.transitTime
            };

            const url = editMode 
                ? `${BACKEND_URL}/countries/${formData.id}` 
                : `${BACKEND_URL}/countries`;
            
            const method = editMode ? 'PUT' : 'POST';
            
            console.log("Sending data to backend:", dataToSend);
            
            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(dataToSend)
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                setMessage(data.message);
                fetchCountries();
                resetForm();
                setTimeout(() => setMessage(''), 3000);
            } else {
                setMessage(data.message || 'An error occurred');
            }
        } catch (error) {
            setMessage('Error: ' + error.message);
        } finally {
            setLoadingData(false);
        }
    };

    const handleEdit = (country) => {        
        // Set logo preview if exists
        if (country.logoUrl) {
            setLogoPreview(country.logoUrl);
        }
        
        // Set partner logo preview if exists
        if (country.labelPartnerLogoUrl) {
            setPartnerLogoPreview(country.labelPartnerLogoUrl);
        }
        
        // Parse cities
        if (country.citiesToCollect) {
            const citiesArray = country.citiesToCollect.split(',').map(c => c.trim()).filter(c => c);
            setCities(citiesArray);
        } else {
            setCities([]);
        }
        
        // Parse transit times
        if (country.transitTime) {
            try {
                const transitTimeObj = JSON.parse(country.transitTime);
                const transitTimesArray = Object.entries(transitTimeObj).map(([country, days]) => ({
                    country,
                    days
                }));
                setTransitTimes(transitTimesArray);
            } catch (e) {
                setTransitTimes([]);
            }
        } else {
            setTransitTimes([]);
        }
        
        setFormData({
            ...country,
            portalUrl: country.portalUrl || 'https://parcels.shypv.com',
            citiesToCollect: country.citiesToCollect || '',
            transitTime: country.transitTime || '',
            termsAndConditions: country.termsAndConditions || ''
        });
        setEditMode(true);
        setShowForm(true);
        setActiveTab('basic');
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this country?')) {
            return;
        }

        setLoadingData(true);
        try {
            const response = await fetch(`${BACKEND_URL}/countries/${id}`, {
                method: 'DELETE'
            });
            const data = await response.json();
            
            if (data.status === 'success') {
                setMessage(data.message);
                fetchCountries();
                setTimeout(() => setMessage(''), 3000);
            } else {
                setMessage(data.message || 'Error deleting country');
            }
        } catch (error) {
            setMessage('Error: ' + error.message);
        } finally {
            setLoadingData(false);
        }
    };

    const handleSetDefault = async (id) => {
        setLoadingData(true);
        try {
            const response = await fetch(`${BACKEND_URL}/countries/${id}/set-default`, {
                method: 'POST'
            });
            const data = await response.json();
            
            if (data.status === 'success') {
                setMessage(data.message);
                fetchCountries();
                setTimeout(() => setMessage(''), 3000);
            } else {
                setMessage(data.message || 'Error setting default country');
            }
        } catch (error) {
            setMessage('Error: ' + error.message);
        } finally {
            setLoadingData(false);
        }
    };

    const resetForm = () => {
        // Use current country's config as defaults if available
        const defaultWeightUnit = selectedCountry?.weightUnit || 'kg';
        const defaultDimensionUnit = selectedCountry?.dimensionUnit || 'cm';
        const defaultShypvCommission = selectedCountry?.shypvCommission || 2.0;
        const defaultCarrier = selectedCountry?.carrier || 'DHL';
        const defaultCurrency = selectedCountry?.currency || '';
        const defaultPaymentGatewayCurrency = selectedCountry?.paymentGatewayCurrency || '';
        
        setFormData({
            id: null,
            countryName: '',
            portalUrl: 'https://parcels.shypv.com',
            logoUrl: '',
            phone: '',
            whatsapp: '',
            citiesToCollect: '',
            // Carrier-specific rate tables
            dhlExportRateUrl: '',
            dhlImportRateUrl: '',
            fedexExportRateUrl: '',
            fedexImportRateUrl: '',
            upsExportRateUrl: '',
            upsImportRateUrl: '',
            otherExportRateUrl: '',
            otherImportRateUrl: '',
            carrier: defaultCarrier,
            transitTime: '',
            weightUnit: defaultWeightUnit,
            dimensionUnit: defaultDimensionUnit,
            currency: defaultCurrency,
            shypvCommission: defaultShypvCommission,
            paymentGatewayCurrency: defaultPaymentGatewayCurrency,
            labelPartnerLogoUrl: '',
            termsAndConditions: '',
            isActive: 1,
            isDefault: 0
        });
        setLogoPreview(null);
        setPartnerLogoPreview(null);
        setCities([]);
        setTransitTimes([]);
        setCityInput('');
        setTransitTimeCountry('');
        setTransitTimeDays('');
        setCities([]);
        setCityInput('');
        setTransitTimes([]);
        setTransitTimeCountry('');
        setTransitTimeDays('');
        setEditMode(false);
        setShowForm(false);
        setActiveTab('basic');
    };

    return (
        <div className="country-management-container">
            {selectedCountry && (
                <div style={{
                    background: '#e7f3ff',
                    border: '1px solid #4a90e2',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div>
                        <strong>Current Country Portal:</strong> {selectedCountry.countryName} 
                        <span style={{ marginLeft: '10px', color: '#666', fontSize: '14px' }}>
                            ({selectedCountry.portalUrl})
                        </span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                        Currency: {selectedCountry.currency} | 
                        Weight Unit: {selectedCountry.weightUnit} | 
                        Dimension Unit: {selectedCountry.dimensionUnit}
                    </div>
                </div>
            )}
            <div className="admin-header">
                <h1>Country Management</h1>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button 
                        className="btn btn-primary"
                        onClick={() => {
                            resetForm();
                            setShowForm(true);
                        }}
                    >
                        + Add New Country
                    </button>
                    <button 
                        className="btn btn-secondary"
                        onClick={() => {
                            sessionStorage.removeItem('adminToken');
                            sessionStorage.removeItem('adminUser');
                            navigate('/admin/login');
                        }}
                        style={{ marginLeft: '10px' }}
                    >
                        Logout
                    </button>
                </div>
            </div>

            {message && (
                <div className={`alert ${message.includes('Error') ? 'alert-danger' : 'alert-success'}`}>
                    {message}
                </div>
            )}

            {loadingData && (
                <div className="loading-overlay">
                    <img src={loading} alt="Loading..." />
                </div>
            )}

            {showForm && (
                <div className="country-form-modal">
                    <div className="modal-content-country">
                        <div className="modal-header-country">
                            <h2>{editMode ? 'Edit Country' : 'Add New Country'}</h2>
                            <div onClick={resetForm} style={{ cursor: 'pointer' }}>&times;</div>
                        </div>

                        <div className="tabs-container">
                            <div className="tabs">
                                <button 
                                    className={activeTab === 'basic' ? 'tab active' : 'tab'}
                                    onClick={() => setActiveTab('basic')}
                                >
                                    Basic Info
                                </button>
                                <button 
                                    className={activeTab === 'contact' ? 'tab active' : 'tab'}
                                    onClick={() => setActiveTab('contact')}
                                >
                                    Contact
                                </button>
                                <button 
                                    className={activeTab === 'rates' ? 'tab active' : 'tab'}
                                    onClick={() => setActiveTab('rates')}
                                >
                                    Rates & Cities
                                </button>
                                <button 
                                    className={activeTab === 'settings' ? 'tab active' : 'tab'}
                                    onClick={() => setActiveTab('settings')}
                                >
                                    Settings
                                </button>
                                <button 
                                    className={activeTab === 'branding' ? 'tab active' : 'tab'}
                                    onClick={() => setActiveTab('branding')}
                                >
                                    Branding & Terms
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit}>
                            {/* Basic Info Tab */}
                            {activeTab === 'basic' && (
                                <div className="form-section">
                                    <h3>Basic Information</h3>
                                    <div className="form-grid">
                                        <div className="form-group full-width">
                                            <label>Country Name *</label>
                                            <select
                                                name="countryName"
                                                value={formData.countryName}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select Country</option>
                                                {Object.keys(countryList).sort().map((country) => (
                                                    <option key={country} value={country}>
                                                        {country}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group full-width">
                                            <label>Portal URL *</label>
                                            {editMode && (
                                                <small style={{display: 'block', color: '#999', marginBottom: '5px'}}>
                                                    Portal URL cannot be changed in edit mode
                                                </small>
                                            )}
                                            <div className="portal-url-container">
                                                <span className="portal-url-base">
                                                    https://parcels.shypv.com/
                                                </span>
                                                <input
                                                    type="text"
                                                    name="portalUrl"
                                                    value={(formData.portalUrl || 'https://parcels.shypv.com').replace("https://parcels.shypv.com/", "").replace("https://parcels.shypv.com", "")}
                                                    onChange={handleInputChange}
                                                    className="portal-url-suffix-input"
                                                    disabled={editMode}
                                                    placeholder="us (without leading slash)"
                                                />
                                            </div>
                                            <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                                Add country suffix (e.g., us, sa, om) - leave empty for UAE base URL
                                            </small>
                                        </div>
                                        <div className="form-group full-width">
                                            <label>Country Logo</label>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                {logoPreview && (
                                                    <div style={{ 
                                                        border: '2px solid #e0e0e0', 
                                                        borderRadius: '8px', 
                                                        padding: '15px',
                                                        background: '#f8f9fa',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        maxHeight: '150px'
                                                    }}>
                                                        <img 
                                                            src={logoPreview} 
                                                            alt="Logo Preview" 
                                                            style={{ 
                                                                maxWidth: '100%', 
                                                                maxHeight: '120px',
                                                                objectFit: 'contain'
                                                            }} 
                                                        />
                                                    </div>
                                                )}
                                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                    <label 
                                                        htmlFor="logo-upload" 
                                                        className="btn btn-secondary"
                                                        style={{ margin: 0, cursor: 'pointer' }}
                                                    >
                                                        {logoPreview ? 'Change Logo' : 'Upload Logo'}
                                                    </label>
                                                    <input
                                                        id="logo-upload"
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleLogoUpload}
                                                        style={{ display: 'none' }}
                                                    />
                                                    {logoPreview && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() => {
                                                                setLogoPreview(null);
                                                                setFormData({ ...formData, logoUrl: '' });
                                                            }}
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                                <small style={{ color: '#666', fontSize: '12px' }}>
                                                    Recommended: PNG or JPG, max 2MB
                                                </small>
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>Currency *</label>
                                            <select
                                                name="currency"
                                                value={formData.currency}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select Currency</option>
                                                {currencyList.map((curr) => (
                                                    <option key={curr} value={curr}>
                                                        {curr}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Contact Tab */}
                            {activeTab === 'contact' && (
                                <div className="form-section">
                                    <h3>Contact Information</h3>
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label>Phone Number</label>
                                            <input
                                                type="text"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                placeholder="600 50 3327"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>WhatsApp Number</label>
                                            <input
                                                type="text"
                                                name="whatsapp"
                                                value={formData.whatsapp}
                                                onChange={handleInputChange}
                                                placeholder="971501234567"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Rates & Cities Tab */}
                            {activeTab === 'rates' && (
                                <div className="form-section">
                                    <h3>Rates & Cities</h3>
                                    <div className="form-grid">
                                        <div className="form-group full-width">
                                            <label>Cities to Collect From</label>
                                            
                                            {/* Bulk Upload Section */}
                                            <div className="bulk-upload-section">
                                                <div className="bulk-upload-header">
                                                    <div className="bulk-upload-title">
                                                        Bulk Upload Cities
                                                    </div>
                                                    <div className="bulk-upload-subtitle">
                                                        Upload multiple cities at once using CSV file
                                                    </div>
                                                </div>
                                                
                                                <div className="bulk-upload-actions">
                                                    {/* Download Template Button */}
                                                    <button
                                                        type="button"
                                                        className="btn btn-template"
                                                        onClick={handleDownloadCityTemplate}
                                                    >
                                                        Download Template
                                                    </button>

                                                    {/* Upload CSV Button */}
                                                    <label 
                                                        htmlFor="city-csv-upload"
                                                        className="btn btn-upload"
                                                    >
                                                        Upload CSV File
                                                    </label>
                                                    <input
                                                        id="city-csv-upload"
                                                        type="file"
                                                        accept=".csv"
                                                        onChange={handleCityFileUpload}
                                                        style={{ display: 'none' }}
                                                    />

                                                    {/* Clear All Button */}
                                                    {cities.length > 0 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-clear-all"
                                                            onClick={handleClearAllCities}
                                                        >
                                                            Clear All Cities
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="bulk-upload-instructions">
                                                    <strong>Instructions:</strong>
                                                    1. Click "Download Template" to get the CSV format<br/>
                                                    2. Add your city names to the CSV file (one city per line)<br/>
                                                    3. Click "Upload CSV File" to import all cities at once
                                                </div>
                                            </div>

                                            {/* Manual Entry Section */}
                                            <div className="manual-entry-section">
                                                <span className="manual-entry-label">
                                                    Or Add Cities Manually:
                                                </span>
                                                <div style={{ display: 'flex', gap: '10px' }}>
                                                    <input
                                                        type="text"
                                                        value={cityInput}
                                                        onChange={(e) => setCityInput(e.target.value)}
                                                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCity())}
                                                        placeholder="Enter city name"
                                                        style={{ flex: 1 }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary"
                                                        onClick={handleAddCity}
                                                        style={{ whiteSpace: 'nowrap', width: 'auto' }}
                                                    >
                                                        + Add City
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Cities Display */}
                                            <div className="cities-display-container">
                                                {cities.length > 0 ? (
                                                    <>
                                                        <div className="cities-count">
                                                            {cities.length} {cities.length === 1 ? 'city' : 'cities'} added:
                                                        </div>
                                                        {cities.map((city, index) => (
                                                            <div key={index} className="city-tag">
                                                                <span>{city}</span>
                                                                <button
                                                                    type="button"
                                                                    className="city-tag-remove"
                                                                    onClick={() => handleRemoveCity(index)}
                                                                    aria-label="Remove city"
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </>
                                                ) : (
                                                    <div className="cities-empty-state">
                                                        No cities added yet. Add cities manually or upload via CSV template.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>Carrier</label>
                                            <select
                                                name="carrier"
                                                value={formData.carrier}
                                                onChange={handleInputChange}
                                            >
                                                <option value="DHL">DHL</option>
                                                <option value="FedEx">FedEx</option>
                                                <option value="UPS">UPS</option>
                                                <option value="Aramex">Aramex</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                        <div className="form-group full-width">
                                            <label>Transit Time</label>
                                            
                                            {/* Bulk Upload Section */}
                                            <div className="bulk-upload-section" style={{ marginBottom: '20px' }}>
                                                <div className="bulk-upload-header">
                                                    <div className="bulk-upload-title">
                                                        Bulk Upload Transit Times
                                                    </div>
                                                    <div className="bulk-upload-subtitle">
                                                        Upload multiple transit times at once using CSV file
                                                    </div>
                                                </div>
                                                
                                                <div className="bulk-upload-actions">
                                                    {/* Download Template Button */}
                                                    <button
                                                        type="button"
                                                        className="btn btn-template"
                                                        onClick={handleDownloadTransitTimeTemplate}
                                                    >
                                                        Download Template
                                                    </button>

                                                    {/* Upload CSV Button */}
                                                    <label 
                                                        htmlFor="transit-time-csv-upload"
                                                        className="btn btn-upload"
                                                    >
                                                        Upload CSV File
                                                    </label>
                                                    <input
                                                        id="transit-time-csv-upload"
                                                        type="file"
                                                        accept=".csv"
                                                        onChange={handleTransitTimeFileUpload}
                                                        style={{ display: 'none' }}
                                                    />

                                                    {/* Clear All Button */}
                                                    {transitTimes.length > 0 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-clear-all"
                                                            onClick={handleClearAllTransitTimes}
                                                        >
                                                            Clear All Transit Times
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="bulk-upload-instructions">
                                                    <strong>Instructions:</strong>
                                                    1. Click "Download Template" to get the CSV format<br/>
                                                    2. Add your transit times (Country/Region, Transit Time) to the CSV file<br/>
                                                    3. Click "Upload CSV File" to import all transit times at once<br/>
                                                    <small style={{ color: '#6c757d', fontSize: '11px' }}>
                                                        Note: Existing entries with the same country will be updated
                                                    </small>
                                                </div>
                                            </div>

                                            {/* Manual Entry Section */}
                                            <div className="manual-entry-section" style={{ marginBottom: '15px' }}>
                                                <span className="manual-entry-label">
                                                    Or Add Transit Times Manually:
                                                </span>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '10px' }}>
                                                    <select
                                                        value={transitTimeCountry}
                                                        onChange={(e) => setTransitTimeCountry(e.target.value)}
                                                        style={{
                                                            padding: '8px 12px',
                                                            border: '1px solid #ddd',
                                                            borderRadius: '4px',
                                                            fontSize: '14px'
                                                        }}
                                                    >
                                                        <option value="">Select Country/Region</option>
                                                        {Object.entries(allCountries).sort((a, b) => a[1].localeCompare(b[1])).map(([code, name]) => (
                                                            <option key={code} value={name}>{name}</option>
                                                        ))}
                                                    </select>
                                                    <input
                                                        type="text"
                                                        value={transitTimeDays}
                                                        onChange={(e) => setTransitTimeDays(e.target.value)}
                                                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTransitTime())}
                                                        placeholder="e.g., 3-5 days"
                                                        style={{
                                                            padding: '8px 12px',
                                                            border: '1px solid #ddd',
                                                            borderRadius: '4px',
                                                            fontSize: '14px'
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary"
                                                        onClick={handleAddTransitTime}
                                                        style={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        + Add
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Transit Times Display */}
                                            <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '10px' }}>
                                                {transitTimes.length > 0 ? (
                                                    <>
                                                        <div style={{ marginBottom: '10px', fontSize: '13px', color: '#666', fontWeight: '500' }}>
                                                            {transitTimes.length} {transitTimes.length === 1 ? 'transit time' : 'transit times'} configured:
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto' }}>
                                                            {transitTimes.map((tt, index) => (
                                                                <div
                                                                    key={index}
                                                                    style={{
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                        background: 'white',
                                                                        border: '1px solid #dee2e6',
                                                                        borderRadius: '4px',
                                                                        padding: '8px 12px',
                                                                        fontSize: '13px'
                                                                    }}
                                                                >
                                                                    <span><strong>{tt.country}:</strong> {tt.days}</span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveTransitTime(index)}
                                                                        style={{
                                                                            background: 'none',
                                                                            border: 'none',
                                                                            color: '#dc3545',
                                                                            cursor: 'pointer',
                                                                            padding: '2px 6px',
                                                                            fontSize: '18px',
                                                                            lineHeight: '1'
                                                                        }}
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <small style={{ color: '#999', fontSize: '12px' }}>
                                                        No transit times added. Add estimated delivery times for different countries manually or upload via CSV template.
                                                    </small>
                                                )}
                                            </div>
                                        </div>
                                        {/* Carrier-Specific Rate Tables */}
                                        <div className="form-group full-width">
                                            <h4 style={{marginTop: '20px', marginBottom: '15px', color: '#7f3f98'}}>Carrier-Specific Rate Tables</h4>
                                            <small style={{display: 'block', marginBottom: '15px', color: '#666'}}>
                                                Upload rate card Excel files for each carrier. Each file should contain zone-based pricing with weights and rates.
                                                Supports Export/Import and Parcel/Document rates.
                                            </small>
                                        </div>

                                        {/* DHL Rate Tables */}
                                        <div className="form-group full-width" style={{background: '#f9f9f9', padding: '15px', borderRadius: '8px', marginBottom: '15px'}}>
                                            <h5 style={{marginBottom: '15px', color: '#333'}}>DHL Rate Tables</h5>
                                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                                                {/* Export Rates */}
                                                <div>
                                                    <label style={{fontWeight: '600', marginBottom: '10px', display: 'block'}}>Export Rates</label>
                                                    <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                                                        <div>
                                                            <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Parcel (Non-Document)</label>
                                                            <div style={{display: 'flex', gap: '5px', marginBottom: '5px'}}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-template"
                                                                    onClick={() => handleDownloadRateCardTemplate('DHL', 'export', 'parcel')}
                                                                    style={{flex: 1, fontSize: '12px', padding: '8px'}}
                                                                >
                                                                    Download Template
                                                                </button>
                                                                <label htmlFor="dhl-export-parcel-upload" className="btn btn-upload" style={{flex: 1, textAlign: 'center', padding: '8px'}}>
                                                                    {rateCardUploadStatus['DHL_export_parcel'] === 'uploading' ? 'Uploading...' : 
                                                                     rateCardUploadStatus['DHL_export_parcel'] === 'success' ? '✓ Uploaded' :
                                                                     'Upload Excel'}
                                                                </label>
                                                            </div>
                                                            <input
                                                                id="dhl-export-parcel-upload"
                                                                type="file"
                                                                accept=".xlsx,.xls"
                                                                onChange={(e) => handleRateCardUpload('DHL', 'export', 'parcel', e.target.files[0])}
                                                                style={{ display: 'none' }}
                                                                disabled={uploadingRateCard}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Document</label>
                                                            <div style={{display: 'flex', gap: '5px', marginBottom: '5px'}}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-template"
                                                                    onClick={() => handleDownloadRateCardTemplate('DHL', 'export', 'document')}
                                                                    style={{flex: 1, fontSize: '12px', padding: '8px'}}
                                                                >
                                                                    Download Template
                                                                </button>
                                                                <label htmlFor="dhl-export-document-upload" className="btn btn-upload" style={{flex: 1, textAlign: 'center', padding: '8px'}}>
                                                                    {rateCardUploadStatus['DHL_export_document'] === 'uploading' ? 'Uploading...' : 
                                                                     rateCardUploadStatus['DHL_export_document'] === 'success' ? '✓ Uploaded' :
                                                                     'Upload Excel'}
                                                                </label>
                                                            </div>
                                                            <input
                                                                id="dhl-export-document-upload"
                                                                type="file"
                                                                accept=".xlsx,.xls"
                                                                onChange={(e) => handleRateCardUpload('DHL', 'export', 'document', e.target.files[0])}
                                                                style={{ display: 'none' }}
                                                                disabled={uploadingRateCard}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Import Rates */}
                                                <div>
                                                    <label style={{fontWeight: '600', marginBottom: '10px', display: 'block'}}>Import Rates</label>
                                                    <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                                                        <div>
                                                            <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Parcel (Non-Document)</label>
                                                            <div style={{display: 'flex', gap: '5px', marginBottom: '5px'}}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-template"
                                                                    onClick={() => handleDownloadRateCardTemplate('DHL', 'import', 'parcel')}
                                                                    style={{flex: 1, fontSize: '12px', padding: '8px'}}
                                                                >
                                                                    Download Template
                                                                </button>
                                                                <label htmlFor="dhl-import-parcel-upload" className="btn btn-upload" style={{flex: 1, textAlign: 'center', padding: '8px'}}>
                                                                    {rateCardUploadStatus['DHL_import_parcel'] === 'uploading' ? 'Uploading...' : 
                                                                     rateCardUploadStatus['DHL_import_parcel'] === 'success' ? '✓ Uploaded' :
                                                                     'Upload Excel'}
                                                                </label>
                                                            </div>
                                                            <input
                                                                id="dhl-import-parcel-upload"
                                                                type="file"
                                                                accept=".xlsx,.xls"
                                                                onChange={(e) => handleRateCardUpload('DHL', 'import', 'parcel', e.target.files[0])}
                                                                style={{ display: 'none' }}
                                                                disabled={uploadingRateCard}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Document</label>
                                                            <div style={{display: 'flex', gap: '5px', marginBottom: '5px'}}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-template"
                                                                    onClick={() => handleDownloadRateCardTemplate('DHL', 'import', 'document')}
                                                                    style={{flex: 1, fontSize: '12px', padding: '8px'}}
                                                                >
                                                                    Download Template
                                                                </button>
                                                                <label htmlFor="dhl-import-document-upload" className="btn btn-upload" style={{flex: 1, textAlign: 'center', padding: '8px'}}>
                                                                    {rateCardUploadStatus['DHL_import_document'] === 'uploading' ? 'Uploading...' : 
                                                                     rateCardUploadStatus['DHL_import_document'] === 'success' ? '✓ Uploaded' :
                                                                     'Upload Excel'}
                                                                </label>
                                                            </div>
                                                            <input
                                                                id="dhl-import-document-upload"
                                                                type="file"
                                                                accept=".xlsx,.xls"
                                                                onChange={(e) => handleRateCardUpload('DHL', 'import', 'document', e.target.files[0])}
                                                                style={{ display: 'none' }}
                                                                disabled={uploadingRateCard}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* FedEx Rate Tables */}
                                        <div className="form-group full-width" style={{background: '#f9f9f9', padding: '15px', borderRadius: '8px', marginBottom: '15px'}}>
                                            <h5 style={{marginBottom: '15px', color: '#333'}}>FedEx Rate Tables</h5>
                                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                                                {/* Export Rates */}
                                                <div>
                                                    <label style={{fontWeight: '600', marginBottom: '10px', display: 'block'}}>Export Rates</label>
                                                    <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                                                        <div>
                                                            <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Parcel (Non-Document)</label>
                                                            <div style={{display: 'flex', gap: '5px', marginBottom: '5px'}}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-template"
                                                                    onClick={() => handleDownloadRateCardTemplate('FedEx', 'export', 'parcel')}
                                                                    style={{flex: 1, fontSize: '12px', padding: '8px'}}
                                                                >
                                                                    Download Template
                                                                </button>
                                                                <label htmlFor="fedex-export-parcel-upload" className="btn btn-upload" style={{flex: 1, textAlign: 'center', padding: '8px'}}>
                                                                    {rateCardUploadStatus['FedEx_export_parcel'] === 'uploading' ? 'Uploading...' : 
                                                                     rateCardUploadStatus['FedEx_export_parcel'] === 'success' ? '✓ Uploaded' :
                                                                     'Upload Excel'}
                                                                </label>
                                                            </div>
                                                            <input
                                                                id="fedex-export-parcel-upload"
                                                                type="file"
                                                                accept=".xlsx,.xls"
                                                                onChange={(e) => handleRateCardUpload('FedEx', 'export', 'parcel', e.target.files[0])}
                                                                style={{ display: 'none' }}
                                                                disabled={uploadingRateCard}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Document</label>
                                                            <div style={{display: 'flex', gap: '5px', marginBottom: '5px'}}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-template"
                                                                    onClick={() => handleDownloadRateCardTemplate('FedEx', 'export', 'document')}
                                                                    style={{flex: 1, fontSize: '12px', padding: '8px'}}
                                                                >
                                                                    Download Template
                                                                </button>
                                                                <label htmlFor="fedex-export-document-upload" className="btn btn-upload" style={{flex: 1, textAlign: 'center', padding: '8px'}}>
                                                                    {rateCardUploadStatus['FedEx_export_document'] === 'uploading' ? 'Uploading...' : 
                                                                     rateCardUploadStatus['FedEx_export_document'] === 'success' ? '✓ Uploaded' :
                                                                     'Upload Excel'}
                                                                </label>
                                                            </div>
                                                            <input
                                                                id="fedex-export-document-upload"
                                                                type="file"
                                                                accept=".xlsx,.xls"
                                                                onChange={(e) => handleRateCardUpload('FedEx', 'export', 'document', e.target.files[0])}
                                                                style={{ display: 'none' }}
                                                                disabled={uploadingRateCard}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Import Rates */}
                                                <div>
                                                    <label style={{fontWeight: '600', marginBottom: '10px', display: 'block'}}>Import Rates</label>
                                                    <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                                                        <div>
                                                            <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Parcel (Non-Document)</label>
                                                            <div style={{display: 'flex', gap: '5px', marginBottom: '5px'}}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-template"
                                                                    onClick={() => handleDownloadRateCardTemplate('FedEx', 'import', 'parcel')}
                                                                    style={{flex: 1, fontSize: '12px', padding: '8px'}}
                                                                >
                                                                    Download Template
                                                                </button>
                                                                <label htmlFor="fedex-import-parcel-upload" className="btn btn-upload" style={{flex: 1, textAlign: 'center', padding: '8px'}}>
                                                                    {rateCardUploadStatus['FedEx_import_parcel'] === 'uploading' ? 'Uploading...' : 
                                                                     rateCardUploadStatus['FedEx_import_parcel'] === 'success' ? '✓ Uploaded' :
                                                                     'Upload Excel'}
                                                                </label>
                                                            </div>
                                                            <input
                                                                id="fedex-import-parcel-upload"
                                                                type="file"
                                                                accept=".xlsx,.xls"
                                                                onChange={(e) => handleRateCardUpload('FedEx', 'import', 'parcel', e.target.files[0])}
                                                                style={{ display: 'none' }}
                                                                disabled={uploadingRateCard}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Document</label>
                                                            <div style={{display: 'flex', gap: '5px', marginBottom: '5px'}}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-template"
                                                                    onClick={() => handleDownloadRateCardTemplate('FedEx', 'import', 'document')}
                                                                    style={{flex: 1, fontSize: '12px', padding: '8px'}}
                                                                >
                                                                    Download Template
                                                                </button>
                                                                <label htmlFor="fedex-import-document-upload" className="btn btn-upload" style={{flex: 1, textAlign: 'center', padding: '8px'}}>
                                                                    {rateCardUploadStatus['FedEx_import_document'] === 'uploading' ? 'Uploading...' : 
                                                                     rateCardUploadStatus['FedEx_import_document'] === 'success' ? '✓ Uploaded' :
                                                                     'Upload Excel'}
                                                                </label>
                                                            </div>
                                                            <input
                                                                id="fedex-import-document-upload"
                                                                type="file"
                                                                accept=".xlsx,.xls"
                                                                onChange={(e) => handleRateCardUpload('FedEx', 'import', 'document', e.target.files[0])}
                                                                style={{ display: 'none' }}
                                                                disabled={uploadingRateCard}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* UPS Rate Tables */}
                                        <div className="form-group full-width" style={{background: '#f9f9f9', padding: '15px', borderRadius: '8px', marginBottom: '15px'}}>
                                            <h5 style={{marginBottom: '15px', color: '#333'}}>UPS Rate Tables</h5>
                                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                                                {/* Export Rates */}
                                                <div>
                                                    <label style={{fontWeight: '600', marginBottom: '10px', display: 'block'}}>Export Rates</label>
                                                    <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                                                        <div>
                                                            <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Parcel (Non-Document)</label>
                                                            <div style={{display: 'flex', gap: '5px', marginBottom: '5px'}}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-template"
                                                                    onClick={() => handleDownloadRateCardTemplate('UPS', 'export', 'parcel')}
                                                                    style={{flex: 1, fontSize: '12px', padding: '8px'}}
                                                                >
                                                                    Download Template
                                                                </button>
                                                                <label htmlFor="ups-export-parcel-upload" className="btn btn-upload" style={{flex: 1, textAlign: 'center', padding: '8px'}}>
                                                                    {rateCardUploadStatus['UPS_export_parcel'] === 'uploading' ? 'Uploading...' : 
                                                                     rateCardUploadStatus['UPS_export_parcel'] === 'success' ? '✓ Uploaded' :
                                                                     'Upload Excel'}
                                                                </label>
                                                            </div>
                                                            <input
                                                                id="ups-export-parcel-upload"
                                                                type="file"
                                                                accept=".xlsx,.xls"
                                                                onChange={(e) => handleRateCardUpload('UPS', 'export', 'parcel', e.target.files[0])}
                                                                style={{ display: 'none' }}
                                                                disabled={uploadingRateCard}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Document</label>
                                                            <div style={{display: 'flex', gap: '5px', marginBottom: '5px'}}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-template"
                                                                    onClick={() => handleDownloadRateCardTemplate('UPS', 'export', 'document')}
                                                                    style={{flex: 1, fontSize: '12px', padding: '8px'}}
                                                                >
                                                                    Download Template
                                                                </button>
                                                                <label htmlFor="ups-export-document-upload" className="btn btn-upload" style={{flex: 1, textAlign: 'center', padding: '8px'}}>
                                                                    {rateCardUploadStatus['UPS_export_document'] === 'uploading' ? 'Uploading...' : 
                                                                     rateCardUploadStatus['UPS_export_document'] === 'success' ? '✓ Uploaded' :
                                                                     'Upload Excel'}
                                                                </label>
                                                            </div>
                                                            <input
                                                                id="ups-export-document-upload"
                                                                type="file"
                                                                accept=".xlsx,.xls"
                                                                onChange={(e) => handleRateCardUpload('UPS', 'export', 'document', e.target.files[0])}
                                                                style={{ display: 'none' }}
                                                                disabled={uploadingRateCard}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Import Rates */}
                                                <div>
                                                    <label style={{fontWeight: '600', marginBottom: '10px', display: 'block'}}>Import Rates</label>
                                                    <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                                                        <div>
                                                            <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Parcel (Non-Document)</label>
                                                            <div style={{display: 'flex', gap: '5px', marginBottom: '5px'}}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-template"
                                                                    onClick={() => handleDownloadRateCardTemplate('UPS', 'import', 'parcel')}
                                                                    style={{flex: 1, fontSize: '12px', padding: '8px'}}
                                                                >
                                                                    Download Template
                                                                </button>
                                                                <label htmlFor="ups-import-parcel-upload" className="btn btn-upload" style={{flex: 1, textAlign: 'center', padding: '8px'}}>
                                                                    {rateCardUploadStatus['UPS_import_parcel'] === 'uploading' ? 'Uploading...' : 
                                                                     rateCardUploadStatus['UPS_import_parcel'] === 'success' ? '✓ Uploaded' :
                                                                     'Upload Excel'}
                                                                </label>
                                                            </div>
                                                            <input
                                                                id="ups-import-parcel-upload"
                                                                type="file"
                                                                accept=".xlsx,.xls"
                                                                onChange={(e) => handleRateCardUpload('UPS', 'import', 'parcel', e.target.files[0])}
                                                                style={{ display: 'none' }}
                                                                disabled={uploadingRateCard}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Document</label>
                                                            <div style={{display: 'flex', gap: '5px', marginBottom: '5px'}}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-template"
                                                                    onClick={() => handleDownloadRateCardTemplate('UPS', 'import', 'document')}
                                                                    style={{flex: 1, fontSize: '12px', padding: '8px'}}
                                                                >
                                                                    Download Template
                                                                </button>
                                                                <label htmlFor="ups-import-document-upload" className="btn btn-upload" style={{flex: 1, textAlign: 'center', padding: '8px'}}>
                                                                    {rateCardUploadStatus['UPS_import_document'] === 'uploading' ? 'Uploading...' : 
                                                                     rateCardUploadStatus['UPS_import_document'] === 'success' ? '✓ Uploaded' :
                                                                     'Upload Excel'}
                                                                </label>
                                                            </div>
                                                            <input
                                                                id="ups-import-document-upload"
                                                                type="file"
                                                                accept=".xlsx,.xls"
                                                                onChange={(e) => handleRateCardUpload('UPS', 'import', 'document', e.target.files[0])}
                                                                style={{ display: 'none' }}
                                                                disabled={uploadingRateCard}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Other Carrier Rate Tables */}
                                        <div className="form-group full-width" style={{background: '#f9f9f9', padding: '15px', borderRadius: '8px', marginBottom: '15px'}}>
                                            <h5 style={{marginBottom: '15px', color: '#333'}}>Other Carrier Rate Tables</h5>
                                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                                                {/* Export Rates */}
                                                <div>
                                                    <label style={{fontWeight: '600', marginBottom: '10px', display: 'block'}}>Export Rates</label>
                                                    <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                                                        <div>
                                                            <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Parcel (Non-Document)</label>
                                                            <div style={{display: 'flex', gap: '5px', marginBottom: '5px'}}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-template"
                                                                    onClick={() => handleDownloadRateCardTemplate('Other', 'export', 'parcel')}
                                                                    style={{flex: 1, fontSize: '12px', padding: '8px'}}
                                                                >
                                                                    Download Template
                                                                </button>
                                                                <label htmlFor="other-export-parcel-upload" className="btn btn-upload" style={{flex: 1, textAlign: 'center', padding: '8px'}}>
                                                                    {rateCardUploadStatus['Other_export_parcel'] === 'uploading' ? 'Uploading...' : 
                                                                     rateCardUploadStatus['Other_export_parcel'] === 'success' ? '✓ Uploaded' :
                                                                     'Upload Excel'}
                                                                </label>
                                                            </div>
                                                            <input
                                                                id="other-export-parcel-upload"
                                                                type="file"
                                                                accept=".xlsx,.xls"
                                                                onChange={(e) => handleRateCardUpload('Other', 'export', 'parcel', e.target.files[0])}
                                                                style={{ display: 'none' }}
                                                                disabled={uploadingRateCard}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Document</label>
                                                            <div style={{display: 'flex', gap: '5px', marginBottom: '5px'}}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-template"
                                                                    onClick={() => handleDownloadRateCardTemplate('Other', 'export', 'document')}
                                                                    style={{flex: 1, fontSize: '12px', padding: '8px'}}
                                                                >
                                                                    Download Template
                                                                </button>
                                                                <label htmlFor="other-export-document-upload" className="btn btn-upload" style={{flex: 1, textAlign: 'center', padding: '8px'}}>
                                                                    {rateCardUploadStatus['Other_export_document'] === 'uploading' ? 'Uploading...' : 
                                                                     rateCardUploadStatus['Other_export_document'] === 'success' ? '✓ Uploaded' :
                                                                     'Upload Excel'}
                                                                </label>
                                                            </div>
                                                            <input
                                                                id="other-export-document-upload"
                                                                type="file"
                                                                accept=".xlsx,.xls"
                                                                onChange={(e) => handleRateCardUpload('Other', 'export', 'document', e.target.files[0])}
                                                                style={{ display: 'none' }}
                                                                disabled={uploadingRateCard}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Import Rates */}
                                                <div>
                                                    <label style={{fontWeight: '600', marginBottom: '10px', display: 'block'}}>Import Rates</label>
                                                    <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                                                        <div>
                                                            <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Parcel (Non-Document)</label>
                                                            <div style={{display: 'flex', gap: '5px', marginBottom: '5px'}}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-template"
                                                                    onClick={() => handleDownloadRateCardTemplate('Other', 'import', 'parcel')}
                                                                    style={{flex: 1, fontSize: '12px', padding: '8px'}}
                                                                >
                                                                    Download Template
                                                                </button>
                                                                <label htmlFor="other-import-parcel-upload" className="btn btn-upload" style={{flex: 1, textAlign: 'center', padding: '8px'}}>
                                                                    {rateCardUploadStatus['Other_import_parcel'] === 'uploading' ? 'Uploading...' : 
                                                                     rateCardUploadStatus['Other_import_parcel'] === 'success' ? '✓ Uploaded' :
                                                                     'Upload Excel'}
                                                                </label>
                                                            </div>
                                                            <input
                                                                id="other-import-parcel-upload"
                                                                type="file"
                                                                accept=".xlsx,.xls"
                                                                onChange={(e) => handleRateCardUpload('Other', 'import', 'parcel', e.target.files[0])}
                                                                style={{ display: 'none' }}
                                                                disabled={uploadingRateCard}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label style={{fontSize: '13px', color: '#666', marginBottom: '5px', display: 'block'}}>Document</label>
                                                            <div style={{display: 'flex', gap: '5px', marginBottom: '5px'}}>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-template"
                                                                    onClick={() => handleDownloadRateCardTemplate('Other', 'import', 'document')}
                                                                    style={{flex: 1, fontSize: '12px', padding: '8px'}}
                                                                >
                                                                    Download Template
                                                                </button>
                                                                <label htmlFor="other-import-document-upload" className="btn btn-upload" style={{flex: 1, textAlign: 'center', padding: '8px'}}>
                                                                    {rateCardUploadStatus['Other_import_document'] === 'uploading' ? 'Uploading...' : 
                                                                     rateCardUploadStatus['Other_import_document'] === 'success' ? '✓ Uploaded' :
                                                                     'Upload Excel'}
                                                                </label>
                                                            </div>
                                                            <input
                                                                id="other-import-document-upload"
                                                                type="file"
                                                                accept=".xlsx,.xls"
                                                                onChange={(e) => handleRateCardUpload('Other', 'import', 'document', e.target.files[0])}
                                                                style={{ display: 'none' }}
                                                                disabled={uploadingRateCard}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Settings Tab */}
                            {activeTab === 'settings' && (
                                <div className="form-section">
                                    <h3>Settings</h3>
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label>Weight Unit</label>
                                            <select
                                                name="weightUnit"
                                                value={formData.weightUnit}
                                                onChange={handleInputChange}
                                            >
                                                <option value="kg">Kilograms (kg)</option>
                                                <option value="lbs">Pounds (lbs)</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Dimension Unit</label>
                                            <select
                                                name="dimensionUnit"
                                                value={formData.dimensionUnit}
                                                onChange={handleInputChange}
                                            >
                                                <option value="cm">Centimeters (cm)</option>
                                                <option value="inch">Inches (inch)</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Shypv Commission (%) *</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                name="shypvCommission"
                                                value={formData.shypvCommission}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="2.0"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Payment Gateway Currency</label>
                                            <select
                                                name="paymentGatewayCurrency"
                                                value={formData.paymentGatewayCurrency}
                                                onChange={handleInputChange}
                                            >
                                                <option value="">Select Currency</option>
                                                {currencyList.map((curr) => (
                                                    <option key={curr} value={curr}>
                                                        {curr}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group checkbox-group">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    name="isActive"
                                                    checked={formData.isActive === 1}
                                                    onChange={handleInputChange}
                                                />
                                                Active
                                            </label>
                                        </div>
                                        <div className="form-group checkbox-group">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    name="isDefault"
                                                    checked={formData.isDefault === 1}
                                                    onChange={handleInputChange}
                                                />
                                                Set as Default
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Branding & Terms Tab */}
                            {activeTab === 'branding' && (
                                <div className="form-section">
                                    <h3>Branding & Terms</h3>
                                    <div className="form-grid">
                                        <div className="form-group full-width">
                                            <label>Label Partner Logo</label>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                {partnerLogoPreview && (
                                                    <div style={{ 
                                                        border: '2px solid #e0e0e0', 
                                                        borderRadius: '8px', 
                                                        padding: '15px',
                                                        background: '#f8f9fa',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        maxHeight: '150px'
                                                    }}>
                                                        <img 
                                                            src={partnerLogoPreview} 
                                                            alt="Partner Logo Preview" 
                                                            style={{ 
                                                                maxWidth: '100%', 
                                                                maxHeight: '120px',
                                                                objectFit: 'contain'
                                                            }} 
                                                        />
                                                    </div>
                                                )}
                                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                    <label 
                                                        htmlFor="partner-logo-upload" 
                                                        className="btn btn-secondary"
                                                        style={{ margin: 0, cursor: 'pointer' }}
                                                    >
                                                        {partnerLogoPreview ? 'Change Partner Logo' : 'Upload Partner Logo'}
                                                    </label>
                                                    <input
                                                        id="partner-logo-upload"
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handlePartnerLogoUpload}
                                                        style={{ display: 'none' }}
                                                    />
                                                    {partnerLogoPreview && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() => {
                                                                setPartnerLogoPreview(null);
                                                                setFormData({ ...formData, labelPartnerLogoUrl: '' });
                                                            }}
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                                <small style={{ color: '#666', fontSize: '12px' }}>
                                                    Partner logo for shipping labels. Recommended: PNG or JPG, max 2MB
                                                </small>
                                            </div>
                                        </div>
                                        <div className="form-group full-width">
                                            <label>Terms & Conditions</label>
                                            <textarea
                                                name="termsAndConditions"
                                                value={formData.termsAndConditions}
                                                onChange={handleInputChange}
                                                placeholder="Enter terms and conditions..."
                                                rows="8"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="form-actions">
                                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={loadingData}>
                                    {loadingData ? 'Saving...' : (editMode ? 'Update Country' : 'Create Country')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Countries List */}
            <div className="countries-list">
                <h2>Countries List</h2>
                <div className="table-responsive">
                    <table className="countries-table">
                        <thead>
                            <tr>
                                <th>Logo</th>
                                <th>Country</th>
                                <th>Portal URL</th>
                                <th>Currency</th>
                                <th>Status</th>
                                <th>Default</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {countries.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center">No countries found. Add your first country!</td>
                                </tr>
                            ) : (
                                countries.map((country) => (
                                    <tr key={country.id}>
                                        <td>
                                            {country.logoUrl ? (
                                                <img 
                                                    src={country.logoUrl} 
                                                    alt={`${country.countryName} logo`}
                                                    style={{ 
                                                        width: '40px', 
                                                        height: '40px', 
                                                        objectFit: 'contain',
                                                        borderRadius: '4px'
                                                    }}
                                                />
                                            ) : (
                                                <div style={{ 
                                                    width: '40px', 
                                                    height: '40px', 
                                                    background: '#e9ecef',
                                                    borderRadius: '4px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '10px',
                                                    color: '#6c757d'
                                                }}>
                                                    No Logo
                                                </div>
                                            )}
                                        </td>
                                        <td>{country.countryName}</td>
                                        <td>
                                            <a href={country.portalUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px' }}>
                                                {country.portalUrl || 'N/A'}
                                            </a>
                                        </td>
                                        <td>{country.currency || 'N/A'}</td>
                                        <td>
                                            <span className={`badge ${country.isActive ? 'badge-success' : 'badge-danger'}`}>
                                                {country.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td>
                                            {country.isDefault === 1 ? (
                                                <span className="badge badge-primary">Default</span>
                                            ) : (
                                                <button 
                                                    className="btn btn-sm btn-outline"
                                                    onClick={() => handleSetDefault(country.id)}
                                                >
                                                    Set Default
                                                </button>
                                            )}
                                        </td>
                                        <td>
                                            <button 
                                                className="btn btn-sm btn-info"
                                                onClick={() => handleEdit(country)}
                                            >
                                                Edit
                                            </button>
                                            {country.countryName !== 'United Arab Emirates' && (
                                                <button 
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => handleDelete(country.id)}
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

